"use client";

import { useState, type ReactNode } from "react";

// =============================================================================
// SIDE NAV ARROWS
// =============================================================================
// Left/right triangle navigation buttons flanking center content
// Manages hover state internally, syncs with orb hover and keyboard state
// Used by both OrbCard (projects) and ContactCard (contact moons)

interface SideNavArrowsProps {
  onRotateLeft: () => void;
  onRotateRight: () => void;
  orbHoveredSide?: "left" | "right" | null;
  arrowKeyPressed?: "left" | "right" | null;
  accentColor: string; // Brand color for active arrow
  isLightMode?: boolean;
  visible?: boolean; // Controls whether arrows render
  responsive?: boolean; // true = w-10 on mobile, w-16 on desktop (OrbCard)
  children: ReactNode; // Center content zone
  leftLabel?: string; // Aria label for left button
  rightLabel?: string; // Aria label for right button
}

export function SideNavArrows({
  onRotateLeft,
  onRotateRight,
  orbHoveredSide,
  arrowKeyPressed,
  accentColor,
  isLightMode = false,
  visible = true,
  responsive = false,
  children,
  leftLabel = "Previous",
  rightLabel = "Next",
}: SideNavArrowsProps) {
  const [hoveredArrow, setHoveredArrow] = useState<"left" | "right" | null>(null);

  // Combine orb hover, arrow hover, and keyboard press states
  const effectiveLeft = hoveredArrow === "left" || orbHoveredSide === "left" || arrowKeyPressed === "left";
  const effectiveRight = hoveredArrow === "right" || orbHoveredSide === "right" || arrowKeyPressed === "right";

  const defaultColor = isLightMode ? "rgba(0, 0, 0, 0.6)" : "rgba(255, 255, 255, 0.8)";

  // Arrow zone width classes
  const zoneClass = responsive ? "w-10 sm:w-16" : "w-16";
  // Margins for center content to clear the arrow zones
  const marginLeft = visible
    ? (responsive ? (typeof window !== "undefined" && window.innerWidth < 640 ? 40 : 64) : 64)
    : 24;
  const marginRight = marginLeft;

  return (
    <div className="relative pointer-events-auto">
      {/* Left tap zone */}
      {visible && (
        <button
          onClick={(e) => { e.stopPropagation(); onRotateLeft(); }}
          onMouseEnter={() => setHoveredArrow("left")}
          onMouseLeave={() => setHoveredArrow(null)}
          className={`absolute left-0 inset-y-0 ${zoneClass} flex items-center justify-center cursor-pointer z-10`}
          style={{
            opacity: effectiveRight ? 0.3 : (effectiveLeft ? 1 : 0.5),
            transition: "opacity 0.15s ease",
          }}
          aria-label={leftLabel}
        >
          <div style={{
            width: 0,
            height: 0,
            borderTop: "8px solid transparent",
            borderBottom: "8px solid transparent",
            borderRight: `10px solid ${effectiveLeft ? accentColor : defaultColor}`,
            transition: "border-color 0.15s ease",
          }} />
        </button>
      )}

      {/* Right tap zone */}
      {visible && (
        <button
          onClick={(e) => { e.stopPropagation(); onRotateRight(); }}
          onMouseEnter={() => setHoveredArrow("right")}
          onMouseLeave={() => setHoveredArrow(null)}
          className={`absolute right-0 inset-y-0 ${zoneClass} flex items-center justify-center cursor-pointer z-10`}
          style={{
            opacity: effectiveLeft ? 0.3 : (effectiveRight ? 1 : 0.5),
            transition: "opacity 0.15s ease",
          }}
          aria-label={rightLabel}
        >
          <div style={{
            width: 0,
            height: 0,
            borderTop: "8px solid transparent",
            borderBottom: "8px solid transparent",
            borderLeft: `10px solid ${effectiveRight ? accentColor : defaultColor}`,
            transition: "border-color 0.15s ease",
          }} />
        </button>
      )}

      {/* Center content zone - margins clear the arrow zones */}
      <div style={{ marginLeft, marginRight }}>
        {children}
      </div>
    </div>
  );
}
